import { getOperationType } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/common/bulk-action-utils.ts';
import { InvestmentsBulkOperationParams } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageInvestments/types.ts';
import {
	getFinalSelectionCount,
	getTableAllSelected,
	getTableSelectedIds,
} from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils';
import { getTableMeta } from '@dealstack/frontend/shared/components/Table/MantineTable/types';
import { TableOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting.ts';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { GetInvestmentsForMipItem, InvestmentsListQueryDto } from '@dealstack/shared/lib/generated';
import { Table } from '@tanstack/react-table';
import {
	BulkAction,
	BulkActionGroup,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ActionsButton/ActionsButton.types';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';

interface CreateBulkActionsParams {
	mipId: string;
	table: Table<GetInvestmentsForMipItem>;
	isLoading: boolean;
	downloadExcel: (params: InvestmentsBulkOperationParams) => Promise<void>;
	downloadOneSchemaAllocationsTemplate: (
		params: InvestmentsBulkOperationParams
	) => Promise<boolean>;
	onOpenUpdateAllocationsImporter: () => Promise<void>;
	onBulkInvite: (selectedRows: GetInvestmentsForMipItem[]) => void;
	onBulkRelease: (selectedRows: GetInvestmentsForMipItem[]) => void;
	onBulkRevoke: (selectedRows: GetInvestmentsForMipItem[]) => void;
	onBulkVestingTerms: (selectedRows: GetInvestmentsForMipItem[]) => void;
	onBulkTransactionType: (currentTable: Table<GetInvestmentsForMipItem>) => void;
	onBulkInviteSelect?: () => void;
	onBulkReleaseSelect?: () => void;
	onBulkRevokeSelect?: () => void;
	onUpdateAllocationsSelect?: () => void;
	onBulkTransactionTypeSelect?: () => void;
	disableSendInvestorInvitation: boolean;
	hasEditorPermission: boolean;
	showUpdateAllocations: boolean;
	showDownloadExcel: boolean;
	tMipManage: TranslateFunction<I18Namespaces.MIP_MANAGE>;
	dataOperations: TableOperations<InvestmentsListQueryDto>;
}

export const createBulkActions = ({
	mipId,
	table,
	isLoading,
	downloadExcel,
	downloadOneSchemaAllocationsTemplate,
	onOpenUpdateAllocationsImporter,
	onBulkInvite,
	onBulkRelease,
	onBulkRevoke,
	onBulkVestingTerms,
	onBulkTransactionType,
	onBulkInviteSelect,
	onBulkReleaseSelect,
	onBulkRevokeSelect,
	onUpdateAllocationsSelect,
	onBulkTransactionTypeSelect,
	disableSendInvestorInvitation,
	hasEditorPermission,
	showUpdateAllocations,
	showDownloadExcel,
	tMipManage,
	dataOperations,
}: CreateBulkActionsParams): BulkActionGroup<GetInvestmentsForMipItem> => {
	const specialPrimary: BulkAction<GetInvestmentsForMipItem>[] = [];
	const primary: BulkAction<GetInvestmentsForMipItem>[] = [];
	const secondary: BulkAction<GetInvestmentsForMipItem>[] = [];
	const special: BulkAction<GetInvestmentsForMipItem>[] = [];

	const getSelectedRows = (currentTable: Table<GetInvestmentsForMipItem>) => {
		const { manualPagination, greedySelecting } = getTableMeta(currentTable);
		const currentData = currentTable.getCoreRowModel().rows.map((r) => r.original);

		if (manualPagination && greedySelecting) {
			const excludedIds = getTableSelectedIds(currentTable);
			return currentData.filter(
				(investment) => !excludedIds.includes(investment.investmentWorkflowId)
			);
		}

		const selectedIds = getTableSelectedIds(currentTable);
		return currentData.filter((investment) =>
			selectedIds.includes(investment.investmentWorkflowId)
		);
	};

	if (showUpdateAllocations) {
		primary.push({
			key: 'update_allocations',
			label: tMipManage('investments.actions.update_allocations'),
			buttonLabel: tMipManage('table.bulk_actions.download'),
			disabled: isLoading,
			onSelect: () => onUpdateAllocationsSelect?.(),
			onExecute: async (currentTable) => {
				const wasDownloaded = await downloadOneSchemaAllocationsTemplate({
					operationType: getOperationType(currentTable),
					mipId,
					table: currentTable,
					dataOperations,
				});
				if (!wasDownloaded) {
					getTableMeta(currentTable).resetActionState?.();
					return;
				}

				await onOpenUpdateAllocationsImporter();
				getTableMeta(currentTable).resetActionState?.();
			},
		});
	}

	primary.push({
		key: 'bulk_transaction_type',
		label: tMipManage('investments.actions.update_transaction_type'),
		disabled: !hasEditorPermission,
		onSelect: () => onBulkTransactionTypeSelect?.(),
		onExecute: (currentTable) => {
			// The list is filtered to non-released while this action is armed, so every
			// selected row is eligible; the backend re-checks regardless.
			onBulkTransactionType(currentTable);
		},
	});

	// Update Actions
	primary.push({
		key: 'bulk_invite',
		label: tMipManage('investors.actions.send_out_invitations'),
		disabled: disableSendInvestorInvitation,
		onSelect: () => onBulkInviteSelect?.(),
		onExecute: (currentTable) => {
			const selectedRows = getSelectedRows(currentTable);
			const eligibleRows = selectedRows.filter((row) => !row.isCompleted);
			onBulkInvite(eligibleRows);
		},
	});

	primary.push({
		key: 'bulk_release',
		label: tMipManage('investors.actions.release'),
		disabled: false,
		onSelect: () => onBulkReleaseSelect?.(),
		onExecute: (currentTable) => {
			const selectedRows = getSelectedRows(currentTable);
			const eligibleRows = selectedRows.filter((row) => row.isEligibleForRelease);
			onBulkRelease(eligibleRows);
		},
	});

	primary.push({
		key: 'bulk_revoke',
		label: tMipManage('investors.actions.revoke'),
		disabled: false,
		onSelect: () => onBulkRevokeSelect?.(),
		onExecute: (currentTable) => {
			const selectedRows = getSelectedRows(currentTable);
			const eligibleRows = selectedRows.filter((row) => !row.isCompleted);
			onBulkRevoke(eligibleRows);
		},
	});

	primary.push({
		key: 'bulk_vesting_terms',
		label: tMipManage('documents_page.table.middle_bar.buttons.vesting_terms'),
		disabled: !hasEditorPermission,
		onExecute: (currentTable) => {
			onBulkVestingTerms(getSelectedRows(currentTable));
		},
	});

	// Download Actions
	if (showDownloadExcel) {
		secondary.push({
			key: 'bulk_download',
			label: tMipManage('investors.actions.download_selection'),
			buttonLabel: () => {
				const { manualPagination, greedySelecting } = getTableMeta(table);
				const isGreedySelection = greedySelecting && manualPagination;

				return getTableAllSelected(table) || isGreedySelection
					? `${tMipManage('table.bulk_actions.download_filtered')} (${getFinalSelectionCount(table)})`
					: tMipManage('table.bulk_actions.download_selection');
			},
			disabled: isLoading,
			onExecute: () => {
				void downloadExcel({
					operationType: getOperationType(table),
					mipId,
					table,
					dataOperations,
				});
			},
		});
	}

	return { specialPrimary, primary, secondary, special };
};
